import type { ComponentProps, ReactNode } from 'react';
import { styled } from 'styled-components';

import type { ApiFilestackImageData } from '#/api/types/ApiFilestackImageData';
import { Box } from '#/components/Box/Box';
import { Form } from '#/components/Form/Form';
import type { FormSubmitFunction } from '#/components/Form/FormContext';
import { InputField } from '#/components/Form/InputField';
import { SubmitButton } from '#/components/Form/SubmitButton/SubmitButton';
import { Body } from '#/components/Text/Body';
import { H2 } from '#/components/Text/H2';
import { useAppConfig } from '#/config/AppConfigContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import { colors, gutterWidth } from '#/theme/theme';
import { imageUrl } from '#/utils/filestack';
import { getStaticImageSrc } from '#/utils/getImageSrc';

function StyledInputField({
  name,
  type,
  label,
  disabled,
}: {
  name: string;
  type?: 'text' | 'tel' | 'email' | 'number' | 'password' | 'url';
  label: string;
  disabled?: boolean;
}) {
  return (
    <InputField
      name={name}
      type={type}
      label={label}
      inlineLabel
      disabled={disabled}
      // @ts-expect-error TS(2322): Type '{ name: string; type: string; label: string;... Remove this comment to see the full error message
      borderRadius="0"
      border="none"
      height="60px"
      pt="16px"
      pb="10px"
    />
  );
}

const StyledSubmitButton = styled(
  ({ ...props }: ComponentProps<typeof SubmitButton>) => (
    // TODO: Fix this the next time the file is edited.
    // oxlint-disable-next-line react/jsx-props-no-spreading
    <SubmitButton {...props} />
  ),
)`
  max-width: 440px;
  box-shadow: 0 0 0 1px white inset;
  background: none;
  color: white;

  &:focus,
  &:hover {
    box-shadow: 0 0 0 1px ${colors.brandGreen} inset;
    background: ${colors.brandGreen};
  }
`;

export type CmsEmailSignupFormValues = {
  firstName?: string;
  lastName?: string;
  email?: string;
};

type Props = {
  headline: string;
  bgImage: ApiFilestackImageData | null | undefined;
  buttonText: string;
  disabled: boolean;
  isSubmitted: boolean;
  onSubmit: FormSubmitFunction<CmsEmailSignupFormValues>;
  qaId?: string;
  preForm?: ReactNode;
};

export function CmsEmailCampaignSignup({
  headline,
  bgImage,
  buttonText,
  disabled,
  isSubmitted,
  onSubmit,
  qaId,
  preForm,
}: Props) {
  const { appConfig } = useAppConfig();
  const { i18n } = useI18n();

  return (
    <div>
      <Box
        p="80px 20px"
        mx={[-gutterWidth / 2, null, -gutterWidth]}
        width="auto !important"
        borderRadius={[0, 0, 0, 24]}
        background={`url("${
          bgImage
            ? imageUrl({
                data: bgImage,
                width: undefined,
                height: undefined,
                fit: 'max',
                insecureSVG: false,
                appConfig,
              })
            : getStaticImageSrc('blue-loops.svg')
        }") center/cover`}
      >
        <Box maxWidth="960px" m="0 auto">
          <H2 color="white" textAlign="center">
            {headline}
          </H2>
        </Box>
        <Form
          analyticsTitle="[cms] Email campaign signup block"
          usageContext="page"
          qaId={qaId}
          onSubmit={onSubmit}
          render={() =>
            isSubmitted ? (
              <Body color="white" textAlign="center" mt="20px">
                {i18n('Thank you for signing up!')}
              </Body>
            ) : (
              <>
                <Box maxWidth="440px" m="40px auto 0">
                  <Box mb="24px">{preForm}</Box>
                  <Box overflow="hidden" borderRadius="4px">
                    <Box display="flex" flexWrap="wrap">
                      <Box width={['100%', null, '50%', null]} flex="0 0 auto">
                        <StyledInputField
                          name="firstName"
                          type="text"
                          label={i18n('First name')}
                          disabled={disabled}
                        />
                      </Box>
                      <Box
                        width={['100%', null, '50%', null]}
                        flex="0 0 auto"
                        borderLeft={[
                          null,
                          null,
                          `1px solid ${colors.elementGrey}`,
                        ]}
                        borderTop={[
                          `1px solid ${colors.elementGrey}`,
                          null,
                          'none',
                        ]}
                      >
                        <StyledInputField
                          name="lastName"
                          type="text"
                          label={i18n('Last name')}
                          disabled={disabled}
                        />
                      </Box>
                    </Box>
                    <Box borderTop={`1px solid ${colors.elementGrey}`}>
                      <StyledInputField
                        name="email"
                        type="email"
                        label={`${i18n('Email')}*`}
                        disabled={disabled}
                      />
                    </Box>
                  </Box>
                </Box>
                <Box display="flex" justifyContent="center" mt="40px">
                  <StyledSubmitButton disabled={disabled}>
                    {buttonText}
                  </StyledSubmitButton>
                </Box>
              </>
            )
          }
        />
      </Box>
    </div>
  );
}
